/**
 * The catalog's API client — categories, services, plans, pricing, checklists,
 * and the workflow a service carries (spec §C, ADR-0009).
 *
 * A client per module, not a growing `lib/api.ts` (waves.md §1.1).
 *
 * **The rules are not duplicated here.** `domain.validate` on the service is
 * the only validator; this file's `groupIssues` sorts what the server said onto
 * the stage card that caused it. Two validators that agree today are two
 * validators that disagree in a year, and the one the firm sees would be the
 * one that is wrong.
 *
 * The pure helpers at the bottom live beside the client rather than inside a
 * component so the screens' rules can be tested without rendering anything —
 * the shape `lib/api/firm.ts` set in Wave 1A.
 *
 * **C25 (Wave 27, waves.md Wave 27 I-5 and I-6)** — categories, services and
 * plans are **active, inactive or deleted** (ADR-0054 §1), set by one
 * `…/status` call each; staff lists take `live · active · inactive · deleted`
 * (`live`, active and inactive, is the default); every field is editable; a
 * plan has its own website switch; and the website's service picker reads the
 * catalogue as `ServiceCatalog`. Wave 2's `…/archive` calls are gone: no screen
 * ever called them, and 27D retires the routes.
 */

import { api } from '../api'
import type { IconSet } from './site'

// -- shapes ------------------------------------------------------------------

export type StageCategory = 'open' | 'in_progress' | 'review' | 'completed' | 'cancelled'
export type ActionKind = 'notify' | 'request_documents' | 'raise_invoice' | 'assign_reviewer'
/** ADR-0054 §1 — the same three states on categories, services and plans. */
export type Lifecycle = 'active' | 'inactive' | 'deleted'
export const LIFECYCLE: Lifecycle[] = ['active', 'inactive', 'deleted']
/** I-5 — what a staff list asks for. `live` is active and inactive together,
 *  everything that is not deleted, and is the service's default. */
export type CatalogueFilter = 'live' | Lifecycle

export interface ServiceCategory {
  id: string
  name: string
  slug: string
  description: string | null
  display_order: number
  status: Lifecycle
  /** When it was last deleted — C25. */
  deleted_at?: string | null
}

export interface Pricing {
  /** A string, both ways. A rupee amount that has been through a JavaScript
   *  `number` is a rupee amount that may have been rounded. */
  amount: string
  currency: string
  billing_cadence: string | null
  gst_treatment: 'taxable' | 'exempt'
  gst_rate: string | null
}

export interface Plan {
  id: string
  service_id: string
  name: string
  slug: string
  description: string | null
  display_order: number
  status: Lifecycle
  pricing: Pricing | null
  /** `false` is *price on request*, not an incomplete record — it is where
   *  every plan an imported template creates starts. */
  priced: boolean
  /** C25, ADR-0054 §5 — whether the firm's website shows it. Absent from a
   *  service before 27D adds it to the answer; read it with `planShown`. */
  site_visible?: boolean
  deleted_at?: string | null
}

export interface AppliesTo {
  kinds?: string[]
  constitutions?: string[]
}

export interface Service {
  id: string
  category_id: string
  name: string
  slug: string
  description: string | null
  image_url: string | null
  icon: string | null
  sac_code: string | null
  is_recurring: boolean
  site_visible: boolean
  /** C27, ADR-0056 §4 — whether an invoice priced from this service starts
   *  with *Tax payable on reverse charge* on. A default the firm sets, never
   *  one ProDesk guesses: no list of reverse-charge services ships (spec
   *  §9-100). Optional: absent from an answer before migration `0047`. */
  reverse_charge?: boolean
  applies_to: AppliesTo
  display_order: number
  status: Lifecycle
  plans?: Plan[]
  deleted_at?: string | null
  /** w27d — C25, ADR-0054 §2: whether the firm **offers** it — it and its
   *  category active — answered by the service's one rule on the staff list
   *  and the record, so no screen re-derives it. */
  offered?: boolean
}

/** I-5 — `GET …/{id}/usage`: what still names a row, for the delete dialog
 *  (ADR-0054 §3). `services` is a category's. */
export interface CatalogueUsage {
  open_cases: number
  booking_flows: number
  site_sections: number
  services?: number
}

/** I-5 — `ca-erp.{category,service,plan}.status_changed`: one key a table,
 *  carrying the move and the row as it is after it (ADR-0054 §6). */
export interface StatusChange {
  from: Lifecycle
  to: Lifecycle
  deleted_at: string | null
}

/** I-6 — the catalogue as the website's service picker reads it: every
 *  service by its slug and category, the categories in the firm's order. */
export interface PickerService {
  slug: string
  name: string
  /** The category's slug, or `null` when it is not in `categories`. */
  category: string | null
}
export interface PickerCategory {
  slug: string
  name: string
  order: number
}
export interface ServiceCatalog {
  services: PickerService[]
  categories: PickerCategory[]
}

/** I-6 — a services item of the render payload gains its category (27E). */
export interface RenderCategory {
  slug: string
  name: string
  order: number
}

export interface ChecklistItem {
  name: string
  is_required: boolean
  accepted_formats: string[]
  help_text: string | null
}

export interface Checklist {
  id?: string
  name: string | null
  description?: string | null
  items: ChecklistItem[]
}

export interface VersionSummary {
  id: string
  version: number
  status: 'draft' | 'published' | 'archived'
  in_use: boolean
  published_at: string | null
  notes: string | null
  /** The service's rule (ADR-0009), answered by the server so the editor does
   *  not re-derive it in TypeScript. */
  editable: boolean
}

export interface StageTask {
  name: string
  role: string | null
}

export interface StageAction {
  kind: ActionKind
  config: Record<string, string>
}

export interface Stage {
  key: string
  name: string
  order: number
  category: StageCategory
  client_visible: boolean
  client_status: string | null
  allowed_roles: string[]
  requires_review: boolean
  on_approve: string | null
  on_reject: string | null
  /** Where the case goes when **a client approves a document on it** — FR-F5,
   *  T-2.21. A stage key in this version, or `null` for "an approval changes
   *  nothing here", which is every stage configured before Wave 6C. Not the
   *  same field as `on_approve`: that one is the *reviewer's* decision
   *  (ADR-0009 §5) — the firm signing off its own work rather than the client
   *  signing off the firm's. */
  advance_on_approval: string | null
  required_tasks: StageTask[]
  actions: StageAction[]
  /** Resolved from the category by the service (ADR-0009 §4) so nothing
   *  re-derives them. */
  effects: string[]
}

export interface Issue {
  stage: string | null
  field: string | null
  message: string
}

export interface Validation {
  ok: boolean
  errors: Issue[]
  warnings: Issue[]
}

export interface WorkflowVersion extends VersionSummary {
  workflow_id: string
  service_id: string
  service_name: string
  entry_stage: string | null
  stages: Stage[]
  validation: Validation
}

export interface WorkflowSummary {
  id: string
  name: string
  service?: { id: string; name: string }
  versions: VersionSummary[]
  published_version_id: string | null
}

export interface Vocabulary {
  /** C23, ADR-0045 §5 — the icon set the service's picker draws from. */
  icons?: IconSet
  stage_categories: Array<{ code: StageCategory; effects: string[] }>
  action_kinds: Array<{ kind: ActionKind; config: string[] }>
  roles: Array<{ code: string; name: string; may_move_a_case: boolean }>
  client_kinds: string[]
  constitutions: string[]
  gst_treatments: string[]
  billing_cadences: string[]
  review_gate_enabled: boolean
}

export interface TemplateSummary {
  profession: string
  label: string
  note: string
  services: number
  categories: number
  is_yours: boolean
}

export interface TemplateService {
  slug: string
  name: string
  category: string
  description: string
  is_recurring: boolean
  applies_to: AppliesTo
  plans: Array<{ name: string }>
  workflow: string | null
  stage_count: number
  checklist?: { name: string; items: Array<{ name: string; required: boolean }> }
}

export interface TemplatePreview {
  profession: string
  label: string
  note: string
  categories: Array<{ slug: string; name: string }>
  services: TemplateService[]
}

export interface StageDraft {
  key?: string
  name: string
  category: StageCategory
  client_visible: boolean
  client_status: string | null
  allowed_roles: string[]
  requires_review: boolean
  on_approve: string | null
  on_reject: string | null
  advance_on_approval: string | null
  required_tasks: StageTask[]
  actions: StageAction[]
}

// -- the client --------------------------------------------------------------

export const services = {
  vocabulary: () => api<Vocabulary>('/api/v1/services/vocabulary'),

  /** The catalogue with plans and prices. **`active` by default here**, so
   *  the readers that list what may be chosen — a new case, a new invoice, the
   *  site checklist — keep reading what they always read; the catalogue screen
   *  passes its own filter (I-5: `live` is the service's default). */
  list: (status: CatalogueFilter = 'active') =>
    api<{ services: Service[]; categories: ServiceCategory[] }>(
      `/api/v1/services?status=${status}`,
    ),
  read: (id: string) =>
    api<{
      service: Service
      category: ServiceCategory
      checklist: Checklist | null
      workflow: WorkflowSummary
    }>(`/api/v1/services/${id}`),
  create: (body: {
    category_id: string
    name: string
    description?: string
    sac_code?: string
    is_recurring?: boolean
    site_visible?: boolean
    applies_to?: AppliesTo
    /** C23 — a key of the icon set; omitted for *no icon*. */
    icon?: string | null
  }) => api<Service>('/api/v1/services', { method: 'POST', body }),
  /** Every field of a service, its category and order included (ADR-0054 §4). */
  update: (id: string, body: Partial<Service>) =>
    api<Service>(`/api/v1/services/${id}`, { method: 'PATCH', body }),
  /** I-5 — `active ↔ inactive`; either → `deleted`; `deleted → inactive` only
   *  (restore lands inactive, ADR-0054 §3). */
  setStatus: (id: string, status: Lifecycle) =>
    api<Service>(`/api/v1/services/${id}/status`, { method: 'POST', body: { status } }),
  /** I-5 — what still names the service, for the delete dialog. */
  usage: (id: string) => api<CatalogueUsage>(`/api/v1/services/${id}/usage`),

  categories: (status: CatalogueFilter = 'active') =>
    api<ServiceCategory[]>(`/api/v1/services/categories?status=${status}`),
  createCategory: (body: { name: string; description?: string; display_order?: number }) =>
    api<ServiceCategory>('/api/v1/services/categories', { method: 'POST', body }),
  /** A category's name, description and order (ADR-0054 §4). */
  updateCategory: (id: string, body: { name?: string; description?: string | null; display_order?: number }) =>
    api<ServiceCategory>(`/api/v1/services/categories/${id}`, { method: 'PATCH', body }),
  setCategoryStatus: (id: string, status: Lifecycle) =>
    api<ServiceCategory>(`/api/v1/services/categories/${id}/status`, { method: 'POST', body: { status } }),
  /** What still names the category — `services` is how many it holds. */
  categoryUsage: (id: string) => api<CatalogueUsage>(`/api/v1/services/categories/${id}/usage`),

  createPlan: (
    serviceId: string,
    body: { name: string; description?: string; display_order?: number; pricing?: unknown; site_visible?: boolean },
  ) =>
    api<Plan>(`/api/v1/services/${serviceId}/plans`, { method: 'POST', body }),
  /** A plan's name, description, order, price and website switch (ADR-0054
   *  §4–§5). `clear_pricing` is *price on request*. */
  updatePlan: (
    planId: string,
    body: {
      name?: string
      description?: string | null
      display_order?: number
      pricing?: unknown
      clear_pricing?: boolean
      site_visible?: boolean
    },
  ) => api<Plan>(`/api/v1/services/plans/${planId}`, { method: 'PATCH', body }),
  setPlanStatus: (planId: string, status: Lifecycle) =>
    api<Plan>(`/api/v1/services/plans/${planId}/status`, { method: 'POST', body: { status } }),
  planUsage: (planId: string) => api<CatalogueUsage>(`/api/v1/services/plans/${planId}/usage`),

  checklist: (serviceId: string) =>
    api<Checklist>(`/api/v1/services/${serviceId}/checklist`),
  saveChecklist: (serviceId: string, body: Checklist) =>
    api<Checklist>(`/api/v1/services/${serviceId}/checklist`, { method: 'PUT', body }),

  workflow: (serviceId: string) =>
    api<WorkflowSummary>(`/api/v1/services/${serviceId}/workflow`),
  version: (versionId: string) =>
    api<WorkflowVersion>(`/api/v1/services/versions/${versionId}`),
  /** The **Check** button. `can_view`, because checking changes nothing. */
  check: (versionId: string, stages: StageDraft[]) =>
    api<Validation>(`/api/v1/services/versions/${versionId}/validate`, {
      method: 'POST',
      body: { stages },
    }),
  saveStages: (versionId: string, stages: StageDraft[], notes?: string) =>
    api<WorkflowVersion>(`/api/v1/services/versions/${versionId}`, {
      method: 'PUT',
      body: { stages, notes },
    }),
  publish: (versionId: string) =>
    api<VersionSummary>(`/api/v1/services/versions/${versionId}/publish`, { method: 'POST' }),
  newVersion: (serviceId: string, body: { copy_from?: string; notes?: string } = {}) =>
    api<VersionSummary>(`/api/v1/services/${serviceId}/workflow/versions`, {
      method: 'POST',
      body,
    }),

  templates: () =>
    api<{ profession: string | null; templates: TemplateSummary[] }>('/api/v1/services/templates'),
  template: (profession: string) =>
    api<TemplatePreview>(`/api/v1/services/templates/${profession}`),
  /** **The firm's act.** Everything before this created nothing (FR-C6). */
  importTemplate: (profession: string, slugs: string[] | null) =>
    api<{
      profession: string
      created: Array<{ id: string; slug: string; name: string }>
      skipped: Array<{ slug: string; name: string; reason: string }>
      categories_created: string[]
    }>(`/api/v1/services/templates/${profession}/import`, {
      method: 'POST',
      body: { services: slugs },
    }),
}

// -- pure helpers, so the screens' rules are testable ------------------------

export const CATEGORY_LABELS: Record<StageCategory, string> = {
  open: 'Open',
  in_progress: 'In progress',
  review: 'Review',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

export const ACTION_LABELS: Record<ActionKind, string> = {
  notify: 'Tell the client',
  request_documents: 'Ask for the checklist documents',
  raise_invoice: 'Raise an invoice',
  assign_reviewer: 'Assign a reviewer',
}

/**
 * What a plan costs, as a firm would read it — or the sentence for a plan that
 * has none.
 *
 * *Price on request* is a real answer and the screen says so rather than
 * rendering an empty cell: it is where every plan an imported profession
 * template starts, because the templates ship no fees.
 */
export const LIFECYCLE_LABELS: Record<Lifecycle, string> = {
  active: 'Active',
  inactive: 'Inactive',
  deleted: 'Deleted',
}

/** I-5's moves (ADR-0054 §1, §3): `active ↔ inactive`, either to `deleted`,
 *  and `deleted` back to `inactive` only — restore lands inactive. */
export function nextStatuses(status: Lifecycle): Lifecycle[] {
  if (status === 'active') return ['inactive', 'deleted']
  if (status === 'inactive') return ['active', 'deleted']
  return ['inactive']
}

/** Whether the website shows a plan — on unless the firm switched it off. */
export function planShown(plan: Pick<Plan, 'site_visible'>): boolean {
  return plan.site_visible !== false
}

/** I-6 — the picker's catalogue from a staff list: the categories in the
 *  firm's order, then the services in theirs. Deleted rows are left out. */
export function serviceCatalog(list: { services: Service[]; categories: ServiceCategory[] }): ServiceCatalog {
  const categories = list.categories
    .filter((c) => c.status !== 'deleted')
    .sort((a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name))
  const slugOf = new Map(categories.map((c) => [c.id, c.slug]))
  const rank = new Map(categories.map((c, index) => [c.id, index]))
  const services = list.services
    .filter((s) => s.status !== 'deleted')
    .sort(
      (a, b) =>
        (rank.get(a.category_id) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b.category_id) ?? Number.MAX_SAFE_INTEGER) ||
        a.display_order - b.display_order ||
        a.name.localeCompare(b.name),
    )
  return {
    services: services.map((s) => ({ slug: s.slug, name: s.name, category: slugOf.get(s.category_id) ?? null })),
    categories: categories.map((c) => ({ slug: c.slug, name: c.name, order: c.display_order })),
  }
}

export function priceLabel(plan: Plan): string {
  if (!plan.pricing) return 'Price on request'
  const amount = `₹${Number(plan.pricing.amount).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
  const tax =
    plan.pricing.gst_treatment === 'exempt'
      ? 'exempt'
      : `+ ${Number(plan.pricing.gst_rate ?? 0)}% GST`
  const cadence = plan.pricing.billing_cadence
    ? ` · ${plan.pricing.billing_cadence.replace(/_/g, ' ')}`
    : ''
  return `${amount} ${tax}${cadence}`
}

/** Who a service is for (ADR-0015). **Empty means everyone**, and the screen
 *  says that rather than leaving the row blank. */
export function appliesToLabel(applies: AppliesTo | undefined): string {
  const parts: string[] = []
  if (applies?.kinds?.length) parts.push(applies.kinds.join(' and '))
  if (applies?.constitutions?.length) parts.push(applies.constitutions.join(', '))
  return parts.length ? parts.join(' · ') : 'Every client'
}

/**
 * The server's issues, sorted onto the stage card that caused them.
 *
 * `null` is the key for the workflow as a whole — "a case has to be able to
 * finish" belongs in the banner, not on a card, and putting it on an arbitrary
 * stage would send the firm to fix the wrong one.
 */
export function groupIssues(issues: Issue[]): Map<string | null, Issue[]> {
  const out = new Map<string | null, Issue[]>()
  for (const issue of issues) {
    const list = out.get(issue.stage) ?? []
    list.push(issue)
    out.set(issue.stage, list)
  }
  return out
}

/** Whether the editor's form is live, and the sentence for why it is not. */
export function editability(version: VersionSummary | null): {
  editable: boolean
  reason: string | null
} {
  if (!version) return { editable: false, reason: null }
  if (version.in_use) {
    return {
      editable: false,
      reason:
        'Cases are already running on this version, so it cannot be changed. Create a new version — the cases already open keep the rules they started under.',
    }
  }
  if (version.status === 'archived') {
    return {
      editable: false,
      reason:
        'A later version has replaced this one. It is kept so that the cases pinned to it still read.',
    }
  }
  return { editable: true, reason: null }
}

/**
 * Which template a firm lands on.
 *
 * Their own profession when we know it, **and otherwise the first**. Returning
 * nothing left the screen as four buttons over an empty page for every firm
 * whose profession is unset — which is every firm that has not finished
 * onboarding, and they are exactly the firms the screen is for. Found by
 * opening it as a real user, 2026-09-10.
 */
export function defaultTemplate(templates: TemplateSummary[]): string | null {
  const mine = templates.find((t) => t.is_yours)
  return (mine ?? templates[0])?.profession ?? null
}

/** A stage's key, derived from its name and made unique — the same rule the
 *  service applies, so what the editor shows before saving is what comes back
 *  after. A firm never sees or types a key. */
export function stageKey(name: string, taken: string[]): string {
  const base =
    name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64) || 'stage'
  if (!taken.includes(base)) return base
  for (let n = 2; n < 100; n += 1) {
    const candidate = `${base.slice(0, 60)}-${n}`
    if (!taken.includes(candidate)) return candidate
  }
  return base
}

/** A blank stage for the editor's "Add stage". Client-invisible by default:
 *  showing a client something is a decision, and the default should not make
 *  it on the firm's behalf. */
export function blankStage(taken: string[], roles: string[]): StageDraft {
  return {
    key: stageKey('New stage', taken),
    name: 'New stage',
    category: 'in_progress',
    client_visible: false,
    client_status: null,
    allowed_roles: roles,
    requires_review: false,
    on_approve: null,
    on_reject: null,
    advance_on_approval: null,
    required_tasks: [],
    actions: [],
  }
}

/** Stages as the editor holds them, from a saved version. */
export function toDrafts(stages: Stage[]): StageDraft[] {
  return stages.map((stage) => ({
    key: stage.key,
    name: stage.name,
    category: stage.category,
    client_visible: stage.client_visible,
    client_status: stage.client_status,
    allowed_roles: [...stage.allowed_roles],
    requires_review: stage.requires_review,
    on_approve: stage.on_approve,
    on_reject: stage.on_reject,
    advance_on_approval: stage.advance_on_approval,
    required_tasks: stage.required_tasks.map((t) => ({ ...t })),
    actions: stage.actions.map((a) => ({ ...a, config: { ...a.config } })),
  }))
}

/** Move a stage up or down. The order is the list order and the server
 *  renumbers from it, so nothing here has to keep a separate index. */
export function reorder<T>(items: T[], from: number, to: number): T[] {
  if (to < 0 || to >= items.length || from < 0 || from >= items.length) return items
  const next = [...items]
  const moved = next.splice(from, 1)
  next.splice(to, 0, ...moved)
  return next
}
